import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, fetchUser } = useAuth();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const hasHandledRef = useRef(false);

  useEffect(() => {
    if (hasHandledRef.current) {
      return;
    }
    hasHandledRef.current = true;

    const handleCallback = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state'); // Check if this is a connect flow

      if (!code) {
        setError('No authorization code received');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        // Check if this is a GitHub connect flow (state=connect)
        if (state === 'connect') {
          const token = localStorage.getItem('token');
          if (!token) {
            setError('Not authenticated. Please log in first.');
            setTimeout(() => navigate('/login'), 3000);
            return;
          }
          
          const response = await axios.post(
            `${API}/auth/github/connect/callback?code=${code}`,
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          
          setSuccess(true);
          // Refresh user data
          await fetchUser();
          setTimeout(() => {
            navigate('/organization');
          }, 2000);
          return;
        }

        // Try GitHub login first, then Google
        let response;
        let error;
        try {
          response = await axios.post(`${API}/auth/github/callback?code=${code}`);
        } catch (githubErr) {
          error = githubErr;
          // If GitHub fails, try Google
          try {
            response = await axios.post(`${API}/auth/google/callback?code=${code}`);
            error = null;
          } catch (googleErr) {
            error = googleErr;
          }
        }
        
        if (error || !response) {
          throw error || new Error('Authentication failed');
        }
        
        const { access_token, user } = response.data;
        login(access_token, user);
        navigate('/');
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.response?.data?.detail || 'Authentication failed. Please try again.');
        setTimeout(() => {
          if (state === 'connect') {
            navigate('/organization');
          } else {
            navigate('/login');
          }
        }, 3000);
      }
    };

    handleCallback();
  }, [location, navigate, login, fetchUser]);

  const params = new URLSearchParams(location.search);
  const state = params.get('state');
  const isConnectFlow = state === 'connect';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div style={{ background: 'white', borderRadius: '24px', padding: '48px', maxWidth: '400px', textAlign: 'center' }}>
        {error ? (
          <div>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ fontSize: '20px', color: '#ef4444', marginBottom: '8px' }}>{error}</h2>
            <p style={{ fontSize: '14px', color: '#64748b' }}>
              {isConnectFlow ? 'Redirecting to settings...' : 'Redirecting to login...'}
            </p>
          </div>
        ) : success ? (
          <div>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <h2 style={{ fontSize: '20px', color: '#10b981', marginBottom: '8px' }}>GitHub Connected!</h2>
            <p style={{ fontSize: '14px', color: '#64748b' }}>Your repositories are being synced...</p>
          </div>
        ) : (
          <div>
            <div className="loader" style={{ margin: '0 auto 16px' }}></div>
            <h2 style={{ fontSize: '20px', color: '#1e293b', marginBottom: '8px' }}>
              {isConnectFlow ? 'Connecting GitHub...' : 'Completing sign in...'}
            </h2>
            <p style={{ fontSize: '14px', color: '#64748b' }}>Please wait</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
